import moment from "moment-timezone";

// The date/time pickers store wall-clock values ("YYYY-MM-DDTHH:mm") with no
// timezone info. We interpret that wall-clock time as local to the member's
// timeZone (returned by the trips search) and convert it to a UTC ISO string
// for the API. Falls back to the browser's local zone if none was passed.
export function toIsoInTimeZone(localDateTimeString, timeZone) {
  if (!localDateTimeString) return null;
  const parsed = timeZone
    ? moment.tz(localDateTimeString, "YYYY-MM-DDTHH:mm", timeZone)
    : moment(localDateTimeString, "YYYY-MM-DDTHH:mm");
  return parsed.isValid() ? parsed.toISOString() : null;
}

// When "Will Call" is checked, the exact pickup time isn't known — the
// facility calls dispatch when the member is ready — so the UI already pins
// the field's time-of-day to 23:59; this re-derives 23:59:00 straight from
// the chosen date so the payload is exact regardless of what's in the field.
function endOfDayIsoInTimeZone(localDateTimeString, timeZone) {
  if (!localDateTimeString) return null;
  const dateOnly = localDateTimeString.slice(0, 10);
  const parsed = timeZone
    ? moment.tz(`${dateOnly} 23:59:00`, "YYYY-MM-DD HH:mm:ss", timeZone)
    : moment(`${dateOnly} 23:59:00`, "YYYY-MM-DD HH:mm:ss");
  return parsed.isValid() ? parsed.toISOString() : null;
}

// Builds the optional return leg for the tripLegs array: pickup happens at
// the outbound trip's dropoff (facility) and dropoff happens at the outbound
// trip's pickup (home). The address-additional info is swapped from the
// outbound trip's, since it describes those same two locations. No separate
// dropoff time is collected for the return leg, so its appointment time
// mirrors its pickup time.
function buildReturnTripLeg(
  values,
  { timeZone, outboundOriginAdditional, outboundDestinationAdditional }
) {
  if (!values.isReturnTrip) return [];

  const returnPickupIso = values.returnIsWillCall
    ? endOfDayIsoInTimeZone(values.returnPickupDateTime, timeZone)
    : toIsoInTimeZone(values.returnPickupDateTime, timeZone);

  return [
    {
      jobOriginAddress: values.returnPickupAddress?.trim() || "",
      jobOriginLatitude: values.returnPickupAddressLat ?? null,
      jobOriginLongitude: values.returnPickupAddressLng ?? null,
      zipCodeOrg: values.returnPickupAddressZip || "",
      serviceArea: values.returnServiceArea ?? null,
      jobDestinationAddress: values.returnDropoffAddress?.trim() || "",
      jobDestinationLatitude: values.returnDropoffAddressLat ?? null,
      jobDestinationLongitude: values.returnDropoffAddressLng ?? null,
      zipCodeDest: values.returnDropoffAddressZip || "",
      serviceAreaDest: values.returnServiceAreaDest ?? null,
      preScheduleTime: returnPickupIso,
      scheduleTime: returnPickupIso,
      preAppointmentTime: returnPickupIso,
      appointmentTime: returnPickupIso,
      companyNote: "",
      isWillCall: !!values.returnIsWillCall,
      originAddressAdditional: outboundDestinationAdditional,
      destinationAddressAdditional: outboundOriginAdditional,
      appointmentDirection: "",
    },
  ];
}

// Maps the Create Trip form values into the shape the /api/assigns endpoint
// expects. When `values.isReturnTrip` is set, the return leg is embedded in
// `tripLegs` so the whole round trip is created with a single API call.
export function buildCreateTripPayload(
  values,
  { fundingsourceId, memberId, timeZone, client, gender } = {}
) {
  const pickupIso = toIsoInTimeZone(values.pickupDateTime, timeZone);
  const dropoffIso = toIsoInTimeZone(values.dropoffTime, timeZone);

  const originAddressAdditional = {
    modifier: null,
    modifierTwo: "",
    floor: "",
    room: "",
    aptOrSuite: "",
    place: "",
    phone: values.mobileNumber.trim(),
  };
  const destinationAddressAdditional = {
    modifier: null,
    modifierTwo: "",
    floor: "",
    room: "",
    aptOrSuite: "",
    place: "",
    phone: "",
  };

  return {
    cooperate: fundingsourceId,
    client: client || null,
    payorId: null,
    rawId: "",
    tripId: "",
    source: "Member Link",
    eligibilityStatus: "pending",
    jobType: "cooperate",
    isMedical: false,
    companyType: "",
    los: values.levelOfService || null,
    losDetails: {},
    spaceType: values.spaceType,
    spaceTypeDetails: {},
    serviceArea: values.serviceArea ?? null,
    serviceAreaDest: values.serviceAreaDest ?? null,
    jobOriginAddress: values.pickupAddress?.trim() || "",
    jobOriginLatitude: values.pickupAddressLat ?? null,
    jobOriginLongitude: values.pickupAddressLng ?? null,
    zipCodeOrg: values.pickupAddressZip || "",
    jobDestinationAddress: values.dropoffAddress.trim(),
    jobDestinationLatitude: values.dropoffAddressLat ?? null,
    jobDestinationLongitude: values.dropoffAddressLng ?? null,
    zipCodeDest: values.dropoffAddressZip || "",
    priorityClient: {
      displayName: values.displayName.trim(),
      contactNumber: values.mobileNumber.trim(),
      cellPhone: "",
      clientId: memberId,
      email: "",
      preDob: "",
      dob: "",
      weight: "",
      gender: gender || "",
    },
    originAddressAdditional,
    destinationAddressAdditional,
    preScheduleTime: pickupIso,
    scheduleTime: pickupIso,
    preAppointmentTime: dropoffIso,
    appointmentTime: dropoffIso,
    preActualAppointmentTime: dropoffIso,
    actualAppointmentTime: dropoffIso,
    milage: null,
    tripLegs: buildReturnTripLeg(values, {
      timeZone,
      outboundOriginAdditional: originAddressAdditional,
      outboundDestinationAdditional: destinationAddressAdditional,
    }),
    addonServices: [],
    noOfEscorts: "",
    tripReason: [],
    ipa: "",
    primaryDiagnosis: "",
    companyNote: values.driverNotes || "",
    dispatchNoteTwo: "",
    attachment: "",
    preRecurringEndDate: null,
    recurringEndDate: "",
    isStandingOrder: false,
    isRecurring: false,
    driver: null,
    events: [],
    isVipTrip: false,
    isConfirmedTrip: false,
    authorizationNumber: values.authorizationNumber?.trim() || "",
    isWillCall: false,
    appointmentDirection: "",
    attendees: null,
    isAnHourException: false,
  };
}
